/**
 * Pomocniki wyświetlania tabel osadzonych w formularzu (układ kolumn z
 * `element.settings.tableColumns` — kontrakt w `@saas/shared` `embedded-table-columns`).
 *
 * Wspólne dla rendererów na karcie rekordu i dla edytora układu w kreatorze —
 * jedno źródło reguł wyrównania, formatu liczby dziesiętnej i szerokości kolumn.
 */

import type { EmbeddedTableColumnAlign } from '@saas/shared';

/**
 * Szerokość bazowa (px) kolumny BEZ jawnej szerokości w tabeli, w której choć jedna kolumna
 * ją ma (`table-layout: fixed`). Służy WYŁĄCZNIE do policzenia minimalnej szerokości tabeli
 * (`fixedTableMinWidth`): przy układzie stałym przeglądarka nie liczy szerokości z treści,
 * więc gdy tabela robi się węższa niż suma jawnych szerokości, kolumny auto zapadałyby się do
 * 0 px. Minimalna szerokość sprawia, że wtedy tabela rośnie i kontener się przewija.
 * `<col>` kolumny auto zostaje BEZ szerokości — inaczej jawne szerokości przestałyby być
 * pikselami (przy tabeli szerszej niż suma przeglądarka rozdziela nadmiar na WSZYSTKIE kolumny
 * z szerokością, więc stają się proporcjami) i uchwyt zmiany szerokości nie szedłby za kursorem.
 */
export const EMBEDDED_TABLE_COLUMN_FALLBACK_WIDTH = 150;

/** Klasa Tailwind wyrównania tekstu komórki/nagłówka. */
export function columnAlignClass(align: EmbeddedTableColumnAlign): string {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return 'text-left';
}

/**
 * Klasa wyrównania TEKSTU WARTOŚCI w kontrolkach list z biblioteki UI (`Dropdown`,
 * `SearchableSelect` — prop `valueClassName`): sama klasa na komórce nie dosięga tekstu w
 * przycisku kontrolki. `flex-1` rozciąga element z tekstem na całą szerokość (w `Dropdown` bez
 * tego `text-*` nic nie zmienia). Brak wyrównania albo `left` → `undefined` (wygląd domyślny
 * kontrolki, tekst do lewej). Pełne stałe napisy — Tailwind składa arkusz ze znalezionych klas.
 */
export function listControlValueAlignClass(
  align: EmbeddedTableColumnAlign | undefined,
): string | undefined {
  if (align === 'right') return 'flex-1 text-right';
  if (align === 'center') return 'flex-1 text-center';
  return undefined;
}

/**
 * To samo dla chipów wielokrotnego wyboru (`MultiSelect` — `valueClassName` kontenera wartości,
 * który jest kontenerem flex): wyrównanie przez `justify-*`, bo chipy to elementy flex, nie tekst.
 */
export function multiSelectValueAlignClass(
  align: EmbeddedTableColumnAlign | undefined,
): string | undefined {
  if (align === 'right') return 'justify-end';
  if (align === 'center') return 'justify-center';
  return undefined;
}

/**
 * Zwykły zapis dziesiętny (bez szesnastkowych/„Infinity”): opcjonalny znak, cyfry, ułamek,
 * wykładnik. Grupy: 1 = część całkowita, 2 = ułamek (z kropką) po części całkowitej,
 * 3 = ułamek (z kropką) zapisu BEZ części całkowitej, np. „.5”, 4 = wykładnik.
 */
const PLAIN_DECIMAL_RE = /^[+-]?(?:(\d+)(\.\d*)?|(\.\d+))(?:[eE]([+-]?\d+))?$/;

/**
 * Najwyżej tyle cyfr znaczących zapisu dziesiętnego przechodzi przez `number` (i z powrotem na
 * tekst) bez zmiany wyświetlanych cyfr; dalsze cyfry rozstrzygają o zaokrągleniu widocznej,
 * a po konwersji ich nie ma.
 */
const MAX_SAFE_SIGNIFICANT_DIGITS = 15;

/**
 * Czy zapis dziesiętny ma najwyżej `MAX_SAFE_SIGNIFICANT_DIGITS` cyfr znaczących — wtedy
 * konwersja na `number` nie zmieni żadnej cyfry, którą zobaczy użytkownik. Cyfry znaczące
 * liczone bez zer wiodących i końcowych (wykładnik ich liczby nie zmienia). Liczą się WSZYSTKIE
 * cyfry zapisu, także dalsze niż widoczne miejsca: to one decydują o zaokrągleniu ostatniej
 * widocznej cyfry (np. `0.12499999999999999999` przy 2 miejscach to „0,12”, a po zaokrągleniu
 * przez `number` — „0,13”).
 */
function hasSafeSignificantDigits(plainDecimal: string): boolean {
  const match = PLAIN_DECIMAL_RE.exec(plainDecimal);
  if (!match) return false;
  const digits = ((match[1] ?? '') + (match[2] ?? match[3] ?? '')).replace('.', '');
  const significant = digits.replace(/^0+/, '').replace(/0+$/, '');
  return significant.length <= MAX_SAFE_SIGNIFICANT_DIGITS;
}

/**
 * `Intl.NumberFormat#format` przyjmujące TEKST dziesiętny (ES2023, „Intl.NumberFormat v3”):
 * silnik formatuje wtedy dokładnie z zapisu, bez pośredniej konwersji na `number`. Typy TS
 * znają tę przeciążoną sygnaturę tylko dla literałów `${number}`, nie dla zwykłego `string`,
 * a starszy silnik ją po cichu zamienia na `Number(tekst)` — stąd wąskie, jawne zawężenie typu
 * zamiast rzutowania na `any`; czy silnik formatuje tekst naprawdę dokładnie, rozstrzyga sonda.
 */
interface StringAcceptingNumberFormat {
  format(text: string): string;
}

function formatDecimalText(formatter: Intl.NumberFormat, text: string): string {
  return (formatter as unknown as StringAcceptingNumberFormat).format(text);
}

/** Zapis, który po konwersji na `number` zmienia ostatnią widoczną cyfrę (przy 2 miejscach). */
const EXACT_FORMAT_PROBE_TEXT = '0.12499999999999999999';
/** Ten sam zapis bez cyfr, które `number` by zaokrąglił — wynik dokładnego formatowania. */
const EXACT_FORMAT_PROBE_EXPECTED_TEXT = '0.12';

let exactTextFormattingSupported: boolean | undefined;

/** Formattery `pl-PL` per liczba miejsc po przecinku — jeden na `decimals`, nie na komórkę. */
const decimalFormatters = new Map<number, Intl.NumberFormat>();

function decimalFormatter(decimals: number): Intl.NumberFormat {
  let formatter = decimalFormatters.get(decimals);
  if (formatter === undefined) {
    formatter = new Intl.NumberFormat('pl-PL', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    decimalFormatters.set(decimals, formatter);
  }
  return formatter;
}

/**
 * Sonda (raz, wynik w pamięci): czy silnik formatuje tekst dziesiętny dokładnie. Dokładny
 * silnik daje dla zapisu `0.12499999999999999999` to samo, co dla `0.12` (przy 2 miejscach);
 * silnik, który najpierw robi `Number`, zaokrągla do `0.125` i pokazuje „…13”. Porównanie dwóch
 * wyników tego samego formattera nie zależy od separatora dziesiętnego lokalizacji.
 */
function supportsExactTextFormatting(): boolean {
  if (exactTextFormattingSupported === undefined) {
    try {
      const formatter = new Intl.NumberFormat('pl-PL', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      exactTextFormattingSupported =
        formatDecimalText(formatter, EXACT_FORMAT_PROBE_TEXT) ===
        formatDecimalText(formatter, EXACT_FORMAT_PROBE_EXPECTED_TEXT);
    } catch (error) {
      // Silnik odrzucający tekst albo opcje formattera — bezpieczne zachowanie zapasowe (bez
      // dokładnego formatowania tekstu); każdy inny błąd propaguje.
      if (!(error instanceof RangeError) && !(error instanceof TypeError)) throw error;
      exactTextFormattingSupported = false;
    }
  }
  return exactTextFormattingSupported;
}

/**
 * Formattery walutowe `pl-PL` per (waluta, liczba miejsc) — jak `decimalFormatters`; `null` =
 * silnik odrzucił kod waluty (zapamiętane, żeby nie próbować przy każdej komórce).
 */
const currencyFormatters = new Map<string, Intl.NumberFormat | null>();

/**
 * Formatter walutowy albo `null` dla kodu waluty, którego `Intl.NumberFormat` nie przyjmuje.
 * `currency` bywa dowolnym tekstem z bazy (pole tekstowe, nie enum), a niepoprawny kod (pusty,
 * `XX1`) rzuca `RangeError` — bez przechwycenia jedna zła wartość ubiłaby render całej tabeli.
 * Każdy inny błąd propaguje.
 */
function currencyFormatter(currency: string, decimals: number): Intl.NumberFormat | null {
  const key = `${currency}:${decimals}`;
  const cached = currencyFormatters.get(key);
  if (cached !== undefined) return cached;
  let formatter: Intl.NumberFormat | null;
  try {
    formatter = new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  } catch (error) {
    if (!(error instanceof RangeError)) throw error;
    formatter = null;
  }
  currencyFormatters.set(key, formatter);
  return formatter;
}

/**
 * Czy `Intl.NumberFormat` przyjmuje ten kod waluty (styl `currency`). Dla wołających, którzy
 * formatują kwotę sami i przy niepoprawnym kodzie pokazują surową wartość z kodem.
 */
export function isFormattableCurrencyCode(currency: string): boolean {
  return currencyFormatter(currency, 2) !== null;
}

/** Tylko dla testów: zapomina wynik sondy i formattery, żeby kolejny test zaczynał od zera. */
export function __resetExactTextFormattingProbeForTests(): void {
  exactTextFormattingSupported = undefined;
  decimalFormatters.clear();
  currencyFormatters.clear();
}

/**
 * Wspólny rdzeń formatowania dziesiętnego (zwykłego i walutowego): liczbę (`number`) formatujemy
 * wprost, zapis TEKSTOWY dokładnie z tekstu, gdy silnik to potrafi, a w przeciwnym razie
 * bezpiecznym zachowaniem zapasowym (patrz `formatDecimalCell`). `null` = nie da się
 * sformatować — wołający pokazuje wtedy surową wartość.
 */
function formatDecimalValue(formatter: Intl.NumberFormat, value: unknown): string | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? formatter.format(value) : null;
  }
  if (typeof value !== 'string') return null;

  const text = value.trim();
  if (text === '' || !PLAIN_DECIMAL_RE.test(text)) return null;
  const asNumber = Number(text);
  // Spoza zakresu `number` (np. `1e999`) dokładny formatter pokazałby symbol nieskończoności.
  if (!Number.isFinite(asNumber)) return null;

  if (supportsExactTextFormatting()) {
    return formatDecimalText(formatter, text);
  }
  if (!hasSafeSignificantDigits(text)) return null;
  return formatter.format(asNumber);
}

/**
 * Formatuje wartość kolumny dziesiętnej z zadaną liczbą miejsc po przecinku
 * (tylko wyświetlanie — wartość w bazie bez zmian). `decimals` brak → `null`,
 * wołający zostaje przy swoim dotychczasowym formacie. Wartość pusta (także same spacje),
 * nieliczbowa, nieskończona albo spoza zakresu `number` (np. `1e999`) → `null`.
 *
 * Liczbę (`number`) formatujemy wprost. Zapis TEKSTOWY (pg zwraca `numeric` jako string)
 * formatujemy DOKŁADNIE z tekstu (`Intl.NumberFormat#format(tekst)`), gdy silnik to potrafi —
 * pośrednie `Number` zaokrągliłoby wcześniej i mogło zmienić ostatnią widoczną cyfrę
 * (`0.12499999999999999999` przy 2 miejscach to „0,12”, nie „0,13”). Gdy silnik tego nie
 * potrafi (sonda), zachowanie zapasowe jest bezpieczne: tekst o więcej niż 15 cyfrach
 * znaczących → `null` (wołający pokazuje wtedy surową wartość), pozostałe przez `Number`.
 *
 * Lokalizacja `pl-PL` wprost (jak w `AdditionalViewTab`) — formatowanie liczb w panelu nie
 * zależy od języka interfejsu.
 */
export function formatDecimalCell(value: unknown, decimals: number | undefined): string | null {
  if (decimals === undefined) return null;
  return formatDecimalValue(decimalFormatter(decimals), value);
}

/**
 * To samo co `formatDecimalCell`, ale ze znakiem waluty (`Intl.NumberFormat` w stylu `currency`,
 * `pl-PL`): stała liczba miejsc `decimals`, wynik np. „1 234,5000 zł”. Dokładne formatowanie
 * z tekstu (kwoty i ceny jednostkowe `numeric(18,6)` mogą mieć ponad 15 cyfr znaczących) ma tę
 * samą sondę i to samo zachowanie zapasowe co wersja bez waluty. UWAGA: sonda sprawdza formatter
 * dziesiętny (bez waluty); że formatter walutowy idzie tą samą ścieżką formatowania liczby, to
 * ZAŁOŻENIE — nie jest sondowany osobno.
 * `decimals` brak → `null`; kod waluty odrzucony przez `Intl` (pusty, `XX1`) → `null` (wołający
 * pokazuje surową wartość).
 */
export function formatDecimalCurrencyCell(
  value: unknown,
  decimals: number | undefined,
  currency: string,
): string | null {
  if (decimals === undefined) return null;
  const formatter = currencyFormatter(currency, decimals);
  if (formatter === null) return null;
  return formatDecimalValue(formatter, value);
}

/** Czy którakolwiek kolumna ma jawną szerokość (wtedy tabela przechodzi na `table-layout: fixed`). */
export function hasExplicitWidths(columns: readonly { width?: number }[]): boolean {
  return columns.some((c) => c.width !== undefined);
}

/**
 * Minimalna szerokość (px) tabeli w układzie stałym: jawne szerokości + szerokość bazowa
 * (`EMBEDDED_TABLE_COLUMN_FALLBACK_WIDTH`) za każdą kolumnę auto + `trailingWidths`
 * (dodatkowe kolumny techniczne na końcu, np. akcje wiersza). Brak jawnych szerokości →
 * `undefined` (tabela nie przechodzi na układ stały, nie potrzebuje minimum).
 *
 * Wołający ustawia to jako `style={{ minWidth }}` tabeli: gdy kontener jest węższy, tabela
 * rośnie i kontener się przewija, zamiast zapadać kolumny auto do 0 px.
 */
export function fixedTableMinWidth(
  columns: readonly { width?: number }[],
  trailingWidths: readonly number[] = [],
): number | undefined {
  if (!hasExplicitWidths(columns)) return undefined;
  let total = 0;
  for (const column of columns) total += column.width ?? EMBEDDED_TABLE_COLUMN_FALLBACK_WIDTH;
  for (const width of trailingWidths) total += width;
  return total;
}

/**
 * `<colgroup>` dla tabeli z jawną szerokością choć jednej kolumny. Bez żadnej szerokości → `null`
 * (render tabeli bez zmian względem stanu sprzed konfiguracji układu). Kolumna z jawną szerokością
 * dostaje `<col>` z pikselami, kolumna auto — pusty `<col />` (dzieli resztę miejsca tabeli);
 * przed zapadnięciem do 0 px chroni `fixedTableMinWidth` (`minWidth` tabeli), nie szerokość `<col>`.
 * `trailingWidths` — szerokości (px) dodatkowych kolumn technicznych na końcu (np. akcje wiersza).
 */
export function TableColgroup({
  columns,
  trailingWidths = [],
}: {
  columns: readonly { id: string; width?: number }[];
  trailingWidths?: readonly number[];
}) {
  if (!hasExplicitWidths(columns)) return null;
  return (
    <colgroup>
      {columns.map((c) =>
        c.width === undefined ? (
          <col key={c.id} />
        ) : (
          <col key={c.id} style={{ width: `${c.width}px` }} />
        ),
      )}
      {trailingWidths.map((w, i) => (
        <col key={`trailing-${i}`} style={{ width: `${w}px` }} />
      ))}
    </colgroup>
  );
}
